import type Pusher from "pusher-js"
import type { Channel } from "pusher-js"
import { reportClientWarning } from "@/lib/clientReporting"

type PusherConstructor = typeof Pusher
type ReconnectListener = () => void
type ChannelListener = (channel: Channel) => void
type Holder = { channelName: string; onChannel: ChannelListener }

export const PUSHER_LOAD_RETRY_MS = 30_000

const loadPusher = (): Promise<PusherConstructor> =>
    import("pusher-js").then(mod => mod.default)

const scheduleWithTimeout = (run: () => void, delayMs: number): void => {
    setTimeout(run, delayMs)
}

export function createPusherHub({
    load = loadPusher,
    scheduleRetry = scheduleWithTimeout,
}: {
    load?: () => Promise<PusherConstructor>
    scheduleRetry?: (run: () => void, delayMs: number) => void
} = {}) {
    let sharedClient: Pusher | null = null
    let isLoading = false
    let hasConnectedOnce = false
    const reconnectListeners = new Set<ReconnectListener>()
    const channelHolders = new Map<string, number>()
    const waitingHolders = new Set<Holder>()

    function createClient(PusherClient: PusherConstructor): Pusher {
        const client = new PusherClient(
            process.env.NEXT_PUBLIC_PUSHER_KEY || "",
            {
                cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER || "",
                forceTLS: true, // boolean-naming-exempt: external contract — pusher-js option
            }
        )
        client.connection.bind(
            "state_change",
            ({ current }: { previous: string; current: string }) => {
                if (current !== "connected") return
                if (hasConnectedOnce)
                    reconnectListeners.forEach(listen => listen())
                hasConnectedOnce = true
            }
        )
        return client
    }

    function startLoad(): void {
        if (isLoading || sharedClient || waitingHolders.size === 0) return
        isLoading = true
        load().then(
            PusherClient => {
                isLoading = false
                const client = createClient(PusherClient)
                sharedClient = client
                waitingHolders.forEach(holder =>
                    holder.onChannel(client.subscribe(holder.channelName))
                )
                waitingHolders.clear()
            },
            (err: unknown) => {
                isLoading = false
                reportClientWarning({
                    message: "pusher-js failed to load; retrying",
                    cause: err instanceof Error ? err : null,
                    tags: { surface: "pusherClient" },
                })
                if (waitingHolders.size > 0)
                    scheduleRetry(startLoad, PUSHER_LOAD_RETRY_MS)
            }
        )
    }

    function releaseChannel(channelName: string): void {
        const holders = (channelHolders.get(channelName) ?? 0) - 1
        if (holders > 0) {
            channelHolders.set(channelName, holders)
            return
        }
        channelHolders.delete(channelName)
        sharedClient?.unsubscribe(channelName)
    }

    function holdPusherChannel(holder: Holder): () => void {
        let isReleased = false
        channelHolders.set(
            holder.channelName,
            (channelHolders.get(holder.channelName) ?? 0) + 1
        )
        if (sharedClient) {
            holder.onChannel(sharedClient.subscribe(holder.channelName))
        } else {
            waitingHolders.add(holder)
            startLoad()
        }
        return () => {
            if (isReleased) return
            isReleased = true
            waitingHolders.delete(holder)
            releaseChannel(holder.channelName)
        }
    }

    function onPusherReconnect(listener: ReconnectListener): () => void {
        reconnectListeners.add(listener)
        return () => reconnectListeners.delete(listener)
    }

    function isPusherStarted(): boolean {
        return sharedClient !== null
    }

    return { holdPusherChannel, onPusherReconnect, isPusherStarted }
}

export const { holdPusherChannel, onPusherReconnect, isPusherStarted } =
    createPusherHub()
